/* eslint-disable max-lines */
import { Pill, Text } from "@clipboard-health/ui-components";
import { isDefined, isNil } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { API_TYPES } from "@src/appV2/api";
import { useLocalStorage } from "@src/appV2/lib/utils";
import { useSearchParams } from "@src/appV2/lib/utils/useSearchParams";
import { useSetSearchParams } from "@src/appV2/lib/utils/useSetSearchParams";
import { type PlacementCandidate } from "@src/appV2/redesign/PlacementCandidate/types";
import { augmentYearsOfExperienceWithWorkerLicenses } from "@src/appV2/redesign/PlacementCandidate/utils";
import { useCreateWorkerFavoritePlacement } from "@src/appV2/redesign/Placements/api/useCreateWorkerFavoritePlacement";
import { useDeleteWorkerFavoritePlacement } from "@src/appV2/redesign/Placements/api/useDeleteWorkerFavoritePlacement";
import { usePayTransparencyRates } from "@src/appV2/redesign/Placements/lib/usePayTransparencyRates";
import { constructPlacementDataFromWorkerFavoriteResponse } from "@src/appV2/redesign/Placements/utils/worker-favorite-placements";
import { type Worker } from "@src/appV2/Worker/api/types";
import { differenceInDays, parseISO } from "date-fns";
import { Fragment, useEffect } from "react";

import { transformResponseIntoPlacement } from "../api/useFetchPaginatedPlacements";
import {
  EASY_SCHEDULING_BOOKING_DEADLINE_DAYS,
  useGetEasySchedulingPlacements,
} from "../api/useGetEasySchedulingPlacements";
import { useGetWorkerFavoritePlacements } from "../api/useGetWorkerFavoritePlacements";
import {
  type GetPlacementsForWorkplaceQuery,
  PLACEMENT_STATUS_MAP,
} from "../types/fetchPlacements.schema";
import { applicationActiveSectionStorageKey } from "../utils";
import { placementWithDetailsToData } from "../utils/placement-data";
import { AppliedPlacementCard } from "./AppliedPlacementCard/AppliedPlacementCard";
import { InterviewAttendanceFeedbackCard } from "./InterviewAttendanceFeedback/InterviewAttendanceFeedbackCard";
import { PlacementsAccordionSectionV2 } from "./PlacementAccordionSection/PlacementsAccordionSectionV2";
import { PlacementsAccordionSectionV3 } from "./PlacementAccordionSection/PlacementsAccordionSectionV3";
import { PlacementCardV2 } from "./PlacementCard/PlacementCardV2";

interface AppliedPlacementListViewProps {
  placementCandidate: PlacementCandidate;
  worker: Worker;
  filter: GetPlacementsForWorkplaceQuery["filter"];
  showApplications: boolean;
}

export function AppliedPlacementListView(props: AppliedPlacementListViewProps) {
  const { placementCandidate, worker, filter, showApplications } = props;

  const {
    data: easySchedulingData,
    isLoading: isEasySchedulingLoading,
    fetchNextPage: fetchNextEasySchedulingPage,
    hasNextPage: hasNextEasySchedulingPage,
    isFetchingNextPage: isFetchingNextEasySchedulingPage,
  } = useGetEasySchedulingPlacements(
    {
      workerId: worker.userId,
      placementCandidateId: placementCandidate.id,
      shouldLogEvent: false,
    },
    {
      enabled: isDefined(worker.userId) && isDefined(placementCandidate.id),
    }
  );

  const allEasySchedulingPlacements = (easySchedulingData?.pages ?? []).flatMap((page) =>
    page.data.map((placement) => transformResponseIntoPlacement(placement, page.included))
  );

  const easySchedulingPlacements = allEasySchedulingPlacements.filter((placement) => {
    const application = placement.applications?.[0];
    if (!application?.attributes.updatedAt) {
      return true;
    }

    const updatedDate = parseISO(application.attributes.updatedAt);
    const now = new Date();
    const daysSinceUpdated = differenceInDays(now, updatedDate);

    return daysSinceUpdated <= EASY_SCHEDULING_BOOKING_DEADLINE_DAYS;
  });

  const { data: favoritePlacementsData, isLoading: isFavoritePlacementsLoading } =
    useGetWorkerFavoritePlacements(
      {
        workerId: worker.userId,
      },
      {
        enabled: Boolean(isDefined(worker.userId)),
      }
    );

  const queryParameters = useSearchParams<{ applications?: string; interviews?: string }>();
  const setSearchParams = useSetSearchParams();

  const [, setExpandedApplications] = useLocalStorage(
    applicationActiveSectionStorageKey("Sent Applications"),
    false
  );
  const [, setExpandedInterviews] = useLocalStorage(
    applicationActiveSectionStorageKey("Booked Interviews"),
    false
  );

  useEffect(() => {
    if (queryParameters.applications === "true") {
      setExpandedInterviews(false);
      setExpandedApplications(true);
      setSearchParams({ applications: undefined }, { shouldReplace: true });
    }
  }, [queryParameters, setSearchParams, setExpandedApplications, setExpandedInterviews]);

  useEffect(() => {
    if (queryParameters.interviews === "true") {
      setExpandedApplications(false);
      setExpandedInterviews(true);
      setSearchParams({ interviews: undefined }, { shouldReplace: true });
    }
  }, [queryParameters, setSearchParams, setExpandedInterviews, setExpandedApplications]);

  const { getCachedPayRatesForPlacement, isLoading: isPayDataLoading } = usePayTransparencyRates();

  const { mutateAsync: favoritePlacement, isLoading: isFavoritingPlacement } =
    useCreateWorkerFavoritePlacement();

  const { mutateAsync: unfavoritePlacement, isLoading: isUnfavoritingPlacement } =
    useDeleteWorkerFavoritePlacement();

  return (
    <Stack direction="column" spacing={5} sx={{ pb: 10 }}>
      <InterviewAttendanceFeedbackCard placementCandidate={placementCandidate} />

      <PlacementsAccordionSectionV3
        initialExpanded
        title={
          <Stack alignItems="center" direction="row" spacing={2}>
            <Text variant="h5">Pending Interview Invites</Text>
            {easySchedulingPlacements.length > 0 ? (
              <Pill
                label={easySchedulingPlacements.length}
                size="small"
                sx={{
                  backgroundColor: (theme) => theme.palette.error.main,
                  color: (theme) => theme.palette.error.contrastText,
                  padding: 0,
                  minHeight: 20,
                  minWidth: 20,
                  height: "max-content",
                  borderRadius: 10,
                }}
              />
            ) : undefined}
          </Stack>
        }
        isLoading={isEasySchedulingLoading}
        hasNextPage={hasNextEasySchedulingPage ?? false}
        isFetchingNextPage={isFetchingNextEasySchedulingPage}
        emptyText={
          <Text variant="body1" sx={{ textAlign: "center" }}>
            You have no pending interview invites at this time.
          </Text>
        }
        totalPlacementsCount={easySchedulingPlacements.length}
        placementIds={easySchedulingPlacements.map((p) => p.id)}
        renderPlacementCard={(placementId, index) => {
          const placement = easySchedulingPlacements.find((p) => p.id === placementId);

          if (!isDefined(placement)) {
            return null;
          }

          return (
            <Fragment key={placement.id}>
              <AppliedPlacementCard
                placement={placementWithDetailsToData(placement)}
                placementCandidate={placementCandidate}
                pageIndex={0}
                placementIndex={index}
                worker={worker}
                source="easy_scheduling_placements"
              />
            </Fragment>
          );
        }}
        onLoadMore={() => {
          void fetchNextEasySchedulingPage();
        }}
      />

      <PlacementsAccordionSectionV3
        title="Saved Jobs"
        isLoading={isFavoritePlacementsLoading}
        initialExpanded={(favoritePlacementsData?.included?.length ?? 0) > 0}
        hasNextPage={false}
        isFetchingNextPage={false}
        emptyText={
          <Text variant="body1" sx={{ textAlign: "center" }}>
            You have not saved any jobs to your favorites yet!{" "}
          </Text>
        }
        totalPlacementsCount={
          favoritePlacementsData?.included?.filter(
            (included) => included.type === API_TYPES.placement
          ).length ?? 0
        }
        placementIds={
          favoritePlacementsData?.included
            .filter((included) => included.type === API_TYPES.placement)
            .sort((a, b) => {
              const aIsOpen = a.attributes.status === PLACEMENT_STATUS_MAP.OPEN ? 0 : 1;
              const bIsOpen = b.attributes.status === PLACEMENT_STATUS_MAP.OPEN ? 0 : 1;
              return aIsOpen - bIsOpen;
            })
            .map((placement) => placement.id) ?? []
        }
        renderPlacementCard={(placementId, index) => {
          if (isNil(favoritePlacementsData)) {
            return null;
          }

          const placement = favoritePlacementsData.included
            .filter((included) => included.type === API_TYPES.placement)
            .find((placement) => placement.id === placementId);

          if (!isDefined(placement)) {
            return null;
          }

          const yearsOfExperienceByQualification = augmentYearsOfExperienceWithWorkerLicenses(
            placementCandidate.yearsOfExperienceByQualification,
            worker.licenses
          );

          const { minMaxPayEstimate } = getCachedPayRatesForPlacement(
            {
              id: placement.id,
              workplaceId: placement.relationships.workplace.data.id,
              workerTypes: placement.attributes.workerTypes,
              jobShifts: placement.attributes.jobShifts,
              transparencyPayRates: placement.attributes.transparencyPayRates,
              attributes: { payRange: placement.attributes.payRange },
            },
            yearsOfExperienceByQualification,
            false,
            placementCandidate.payAndScheduleFormData?.benefits?.combined
          );

          const workerFavoritePlacementId = favoritePlacementsData.data.find(
            (fav) => fav.relationships.placement.data.id === placement.id
          )?.id;

          return (
            <PlacementCardV2
              workerQualifications={
                new Set(worker.licensesData.map((license) => license.qualification))
              }
              placement={constructPlacementDataFromWorkerFavoriteResponse(
                placement,
                favoritePlacementsData
              )}
              placementCandidateId={placementCandidate.id}
              isRestricted={placementCandidate.isRestricted ?? false}
              pageIndex={0}
              placementIndex={index}
              loadingPayData={isPayDataLoading}
              minMaxPayEstimate={minMaxPayEstimate}
              source="favorite_placements"
              ongoingFavoritingRequest={isFavoritingPlacement || isUnfavoritingPlacement}
              onFavoriteToggle={async (newFavoriteStatus) => {
                if (isFavoritingPlacement || isUnfavoritingPlacement) {
                  return;
                }

                if (newFavoriteStatus) {
                  await favoritePlacement({
                    workerId: worker.userId,
                    placementId: placement.id,
                    source: "favorite_placements_list",
                  });
                } else if (isDefined(workerFavoritePlacementId)) {
                  await unfavoritePlacement({
                    workerId: worker.userId,
                    workerFavoritePlacementId,
                    placementId: placement.id,
                    source: "favorite_placements_list",
                  });
                }
              }}
            />
          );
        }}
      />

      <PlacementsAccordionSectionV2
        hasInterviews
        title="Booked Interviews"
        placementCandidate={placementCandidate}
        worker={worker}
        filter={filter}
        showApplications={showApplications}
      />

      <PlacementsAccordionSectionV2
        title="Sent Applications"
        hasInterviews={false}
        placementCandidate={placementCandidate}
        worker={worker}
        filter={filter}
        showApplications={showApplications}
      />
    </Stack>
  );
}
/* eslint-enable max-lines */
